import { BellOff } from 'lucide-react';
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/atoms/dropdown-menu';
import { DO_NOT_DISTURB_CHOICES } from '@/lib/voip/doNotDisturbDurations';
import type { DoNotDisturbDeviceView } from '@/hooks/voip/useDoNotDisturb';

interface DoNotDisturbMenuItemProps {
  devices: DoNotDisturbDeviceView[];
  /** The web softphone this page is running, if they have one. */
  thisBrowser: DoNotDisturbDeviceView | null;
  /** Zone "tomorrow at 8:00 AM" is worked out in. */
  timeZone?: string | null;
  disabled?: boolean;
  onTurnOn: (deviceId: string, until: Date | null) => void;
  onTurnOff: (deviceId: string) => void;
}

/**
 * Do Not Disturb row for the header's avatar menu.
 *
 * Do Not Disturb is per DEVICE, so the submenu leads with this computer — how
 * long to silence it, and a way to start ringing again — and then lists the
 * person's phones, each with its own switch. That list is how a phone gets
 * silenced from here until the app ships its own control.
 *
 * State and action are never put in the same row. The row that ended the
 * silence used to read "Turn off · Until 8:12 PM", which customers read as an
 * instruction — "turn ringing off until 8:12 PM" — when it meant the opposite.
 * Now a plain line says what is happening ("Calls are silenced until 8:12 PM")
 * and a separate row says what clicking it does ("Turn ringing back on").
 *
 * End times are worked out at the moment of choosing, not when the menu opened,
 * so a menu left open for a while still means "30 minutes from now".
 */
export function DoNotDisturbMenuItem({
  devices,
  thisBrowser,
  timeZone,
  disabled,
  onTurnOn,
  onTurnOff,
}: DoNotDisturbMenuItemProps) {
  const others = devices.filter((device) => !device.isThisBrowser);
  const silenced = devices.filter((device) => device.isOn);
  const summary = () => {
    if (silenced.length === 0) return 'Calls ring on all your devices';
    if (thisBrowser?.isOn && silenced.length === 1) {
      return `This computer is silenced ${thisBrowser.endsLabel ?? 'until you turn ringing back on'}`;
    }
    if (silenced.length === devices.length) return 'Every device is silenced';
    return `Silenced on ${silenced.map((device) => device.label).join(', ')}`;
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={disabled} className="gap-2">
        <BellOff className="h-4 w-4" />
        <span className="flex min-w-0 flex-col">
          <span>Do Not Disturb</span>
          <span className="text-[11px] leading-tight text-muted-foreground">{summary()}</span>
        </span>
      </DropdownMenuSubTrigger>

      <DropdownMenuSubContent className="w-64">
        {thisBrowser ? (
          <>
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              This computer
            </DropdownMenuLabel>
            {thisBrowser.isOn ? (
              <>
                {/* A statement, not a button: it is the only thing in the menu
                    that says what is happening right now. */}
                <DropdownMenuLabel className="pt-0 font-normal text-muted-foreground">
                  Calls are silenced {thisBrowser.endsLabel ?? 'until you turn ringing back on'}.
                </DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={disabled}
                  onSelect={() => onTurnOff(thisBrowser.deviceId)}
                >
                  Turn ringing back on
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Change when it ends
                </DropdownMenuLabel>
              </>
            ) : (
              <DropdownMenuLabel className="pt-0 font-normal text-muted-foreground">
                Stop calls ringing here
              </DropdownMenuLabel>
            )}
            {DO_NOT_DISTURB_CHOICES.map((choice) => (
              <DropdownMenuItem
                key={choice.id}
                disabled={disabled}
                // Read out of the menu's context, "For 1 hour" says nothing
                // about what it does, so the whole sentence is spoken.
                aria-label={choice.announce}
                onSelect={() => onTurnOn(thisBrowser.deviceId, choice.until(new Date(), timeZone))}
              >
                {choice.label}
              </DropdownMenuItem>
            ))}
          </>
        ) : null}

        {others.length > 0 ? (
          <>
            {thisBrowser ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Your other devices
            </DropdownMenuLabel>
            <DropdownMenuLabel className="pt-0 font-normal text-muted-foreground">
              Tick a device to stop it ringing.
            </DropdownMenuLabel>
            {others.map((device) => (
              <DropdownMenuCheckboxItem
                key={device.deviceId}
                checked={device.isOn}
                // A tick means "silenced", which is the opposite of what a tick
                // usually means, so the whole action is spelled out here. The
                // name REPLACES the row's text, so a silenced device has to
                // carry its end time too — otherwise the one thing the menu
                // exists to say is the one thing a screen reader would miss.
                aria-label={
                  device.isOn
                    ? `Turn ringing back on for ${device.label}. Currently silenced ${
                        device.endsLabel ?? 'until you turn ringing back on'
                      }.`
                    : `Silence calls on ${device.label}`
                }
                // A submenu stays open across a save, so without this a second
                // click could send a write that lands after the first and leave
                // the switch showing the older choice.
                disabled={disabled}
                onCheckedChange={(next) =>
                  next === true ? onTurnOn(device.deviceId, null) : onTurnOff(device.deviceId)
                }
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{device.label}</span>
                  {device.isOn ? (
                    <span className="text-[11px] leading-tight text-muted-foreground">
                      Silenced {device.endsLabel ?? 'until you turn ringing back on'}
                    </span>
                  ) : null}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </>
        ) : null}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
